import type { AxisTick } from "klinecharts";

// ---- Weight enum ----

export enum TickMarkWeight {
  LessThanSecond = 0,
  Second         = 10,
  Minute1        = 20,
  Minute5        = 21,
  Minute30       = 22,
  Hour1          = 30,
  Hour3          = 31,
  Hour6          = 32,
  Hour12         = 33,
  Day            = 50,
  Month          = 60,
  Year           = 70,
}

// ---- Date-parts helper (timezone-aware, cached formatters) ----

interface DateParts {
  year: number; month: number; day: number;
  hour: number; minute: number; second: number;
}

const _partsFmtCache = new Map<string, Intl.DateTimeFormat>();

function getPartsFormatter(tz?: string): Intl.DateTimeFormat {
  const key = tz ?? "__local__";
  let fmt = _partsFmtCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      year: "numeric", month: "numeric", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
    _partsFmtCache.set(key, fmt);
  }
  return fmt;
}

function getDateParts(ts: number, tz?: string): DateParts {
  const parts = getPartsFormatter(tz).formatToParts(new Date(ts));
  return {
    year:   +(parts.find(p => p.type === "year")!.value),
    month:  +(parts.find(p => p.type === "month")!.value),
    day:    +(parts.find(p => p.type === "day")!.value),
    hour:   +(parts.find(p => p.type === "hour")!.value) % 24, // guard against "24:00"
    minute: +(parts.find(p => p.type === "minute")!.value),
    second: +(parts.find(p => p.type === "second")!.value),
  };
}

// ---- Weight assignment ----

export function assignWeight(ts: number, prevTs: number, tz?: string): TickMarkWeight {
  const c = getDateParts(ts, tz);
  const p = getDateParts(prevTs, tz);

  if (c.year  !== p.year)  return TickMarkWeight.Year;
  if (c.month !== p.month) return TickMarkWeight.Month;
  if (c.day   !== p.day)   return TickMarkWeight.Day;

  // Same day — intraday boundaries, largest granularity first
  if (Math.floor(c.hour / 12) !== Math.floor(p.hour / 12)) return TickMarkWeight.Hour12;
  if (Math.floor(c.hour /  6) !== Math.floor(p.hour /  6)) return TickMarkWeight.Hour6;
  if (Math.floor(c.hour /  3) !== Math.floor(p.hour /  3)) return TickMarkWeight.Hour3;
  if (c.hour !== p.hour)                                    return TickMarkWeight.Hour1;

  if (Math.floor(c.minute / 30) !== Math.floor(p.minute / 30)) return TickMarkWeight.Minute30;
  if (Math.floor(c.minute /  5) !== Math.floor(p.minute /  5)) return TickMarkWeight.Minute5;
  if (c.minute !== p.minute)                                    return TickMarkWeight.Minute1;

  if (c.second !== p.second) return TickMarkWeight.Second;

  return TickMarkWeight.LessThanSecond;
}

// ---- Label LRU cache ----

function makeLruCache<V>(maxSize: number) {
  const map = new Map<string, V>();
  return {
    get(key: string): V | undefined {
      const val = map.get(key);
      if (val === undefined) return undefined;
      map.delete(key);
      map.set(key, val); // refresh to most-recently-used
      return val;
    },
    set(key: string, val: V): void {
      if (map.has(key)) map.delete(key);
      if (map.size >= maxSize) {
        const lru = map.keys().next().value;
        if (lru !== undefined) map.delete(lru);
      }
      map.set(key, val);
    },
  };
}

const _labelCache = makeLruCache<string>(50);

// ---- Format flags (per timeframe defaults) ----

export interface AxisFormatFlags {
  timeVisible: boolean;
  secondsVisible: boolean;
}

const _flagsByPeriodType: Record<string, AxisFormatFlags> = {
  second: { timeVisible: true,  secondsVisible: true  },
  minute: { timeVisible: true,  secondsVisible: false },
  hour:   { timeVisible: true,  secondsVisible: false },
  day:    { timeVisible: false, secondsVisible: false },
  week:   { timeVisible: false, secondsVisible: false },
  month:  { timeVisible: false, secondsVisible: false },
};

export function getTimeframeAxisFlags(periodType: string): AxisFormatFlags {
  return _flagsByPeriodType[periodType] ?? { timeVisible: false, secondsVisible: false };
}

// ---- Tick label formatter ----

export function formatTickLabel(
  ts: number,
  weight: TickMarkWeight,
  flags: AxisFormatFlags,
  tz?: string,
): string {
  const key = `${ts}|${weight}|${Number(flags.timeVisible)}${Number(flags.secondsVisible)}|${tz ?? ""}`;
  const hit = _labelCache.get(key);
  if (hit !== undefined) return hit;
  const label = _computeLabel(ts, weight, flags, tz);
  _labelCache.set(key, label);
  return label;
}

function _computeLabel(ts: number, weight: TickMarkWeight, flags: AxisFormatFlags, tz?: string): string {
  const d  = new Date(ts);
  const tz_ = tz ? { timeZone: tz } : {};

  if (weight >= TickMarkWeight.Year) {
    return new Intl.DateTimeFormat("en", { ...tz_, year: "numeric" }).format(d);
  }
  if (weight >= TickMarkWeight.Month) {
    return new Intl.DateTimeFormat("en", { ...tz_, month: "short" }).format(d);
  }
  if (weight >= TickMarkWeight.Day) {
    if (flags.timeVisible) {
      // Intraday day boundary: show "22 May" as context anchor
      return new Intl.DateTimeFormat("en", { ...tz_, day: "numeric", month: "short" }).format(d);
    }
    return new Intl.DateTimeFormat("en", { ...tz_, day: "numeric" }).format(d);
  }
  // Intraday time weights (Hour12 → Second)
  if (!flags.timeVisible) {
    return new Intl.DateTimeFormat("en", { ...tz_, day: "numeric" }).format(d);
  }
  if (weight <= TickMarkWeight.Second && flags.secondsVisible) {
    return new Intl.DateTimeFormat("en", {
      ...tz_, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).format(d);
  }
  return new Intl.DateTimeFormat("en", { ...tz_, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
}

// ---- Space-aware tick selection ----

interface WeightedTick extends AxisTick {
  weight: TickMarkWeight;
}

export function buildAxisTicks(
  defaultTicks: readonly AxisTick[],
  opts: {
    barSpacing: number;
    fontSize?: number;
    maxChars?: number;
    flags: AxisFormatFlags;
    tz?: string;
    chartWidth: number;
  },
): AxisTick[] {
  if (defaultTicks.length === 0) return [];

  const { barSpacing, fontSize = 11, maxChars = 8, flags, tz, chartWidth } = opts;

  // Minimum pixel gap between labels
  const pixelsPerChar = (fontSize + 4) * 5 / 8;
  const minGapPx = Math.max(pixelsPerChar * maxChars, barSpacing * 2);

  // Estimate "previous" timestamp for the first tick by extrapolating backward
  const timestamps = defaultTicks.map(t => (typeof t.value === "number" ? t.value : 0));
  const avgDelta = timestamps.length > 1
    ? (timestamps[timestamps.length - 1] - timestamps[0]) / (timestamps.length - 1)
    : 60_000;
  const prevFirst = timestamps[0] - avgDelta;

  // Assign weights
  const weighted: WeightedTick[] = defaultTicks.map((tick, i) => ({
    ...tick,
    weight: assignWeight(
      typeof tick.value === "number" ? tick.value : 0,
      i === 0 ? prevFirst : (typeof defaultTicks[i - 1].value === "number" ? defaultTicks[i - 1].value as number : 0),
      tz,
    ),
  }));

  // Group by weight
  const byWeight = new Map<TickMarkWeight, WeightedTick[]>();
  for (const tick of weighted) {
    const g = byWeight.get(tick.weight) ?? [];
    g.push(tick);
    byWeight.set(tick.weight, g);
  }

  // Iterate weights from highest to lowest
  const sortedWeights = [...byWeight.keys()].sort((a, b) => b - a);

  // Selected set (coord-sorted), seeded with virtual edges outside visible area so edge ticks are never penalised
  const edgeL: WeightedTick = { coord: -minGapPx,           value: 0, text: "", weight: TickMarkWeight.LessThanSecond };
  const edgeR: WeightedTick = { coord: chartWidth + minGapPx, value: 0, text: "", weight: TickMarkWeight.LessThanSecond };
  const selected: WeightedTick[] = [edgeL, edgeR];

  function insertSorted(tick: WeightedTick): void {
    let lo = 0, hi = selected.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (selected[mid].coord < tick.coord) lo = mid + 1;
      else hi = mid;
    }
    selected.splice(lo, 0, tick);
  }

  function findNeighbors(coord: number): [WeightedTick, WeightedTick] {
    let left = edgeL, right = edgeR;
    for (const s of selected) {
      if (s.coord <= coord && s.coord > left.coord) left = s;
      if (s.coord >= coord && s.coord < right.coord) right = s;
    }
    return [left, right];
  }

  for (const w of sortedWeights) {
    for (const tick of byWeight.get(w)!) {
      const [left, right] = findNeighbors(tick.coord);
      if (tick.coord - left.coord >= minGapPx && right.coord - tick.coord >= minGapPx) {
        insertSorted(tick);
      }
    }
  }

  // Strip edges, format text, return sorted
  return selected
    .filter(t => t !== edgeL && t !== edgeR)
    .map(tick => ({
      coord: tick.coord,
      value: tick.value,
      text: formatTickLabel(
        typeof tick.value === "number" ? tick.value : 0,
        tick.weight,
        flags,
        tz,
      ),
    }));
}
